import { EditorContent } from '@tiptap/react';
import { useEffect, useRef, useCallback } from 'react';
import { useCollapse } from 'react-collapsed';
import { useWysiwygEditor } from './hooks/useWysiwygEditor';
import { Toolbar } from './components/Toolbar';
import './WysiwygEditor.css';
import { marked } from 'marked';
interface WysiwygEditorProps {
  content: string;
  onChange: (content: string) => void;
  editable: boolean;
  showToolbar?: boolean;
}
marked.use({
  gfm: true,
  breaks: false
});

const WysiwygEditor = ({ content, onChange, editable, showToolbar }: WysiwygEditorProps) => {
  const lastContentRef = useRef<string>(content);
  const lastOutputHtmlRef = useRef<string>('');

  // Wrap onChange to track the HTML we're outputting
  const wrappedOnChange = useCallback(
    (html: string) => {
      lastOutputHtmlRef.current = html;
      onChange(html);
    },
    [onChange]
  );

  const editor = useWysiwygEditor({ content, onChange: wrappedOnChange, editable });

  // Use react-collapsed to animate the toolbar
  const { getCollapseProps } = useCollapse({
    isExpanded: showToolbar !== undefined ? showToolbar : editable,
    duration: 300
  });

  // Update editor editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  // Only update editor content when it's an external change (not from user typing)
  useEffect(() => {
    if (!editor) return;

    // If content prop hasn't changed, skip (prevents unnecessary updates)
    if (content === lastContentRef.current) {
      return;
    }

    // Determine if content is HTML or markdown by checking for HTML tags
    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    const markdownResult = isHtml ? content : marked(content);

    // Handle both sync and async marked results
    const processHtml = async (incomingHtml: string | Promise<string>) => {
      const html = typeof incomingHtml === 'string' ? incomingHtml : await incomingHtml;
      const currentHtml = editor.getHTML();

      // If incoming content matches the last HTML we output, this is from user typing
      // Skip the update to preserve cursor position
      if (html === lastOutputHtmlRef.current) {
        lastContentRef.current = content;
        return;
      }

      // If incoming HTML matches current editor HTML, also skip (no change needed)
      if (html === currentHtml) {
        lastContentRef.current = content;
        return;
      }

      // This is an external change - update the editor
      // Save current selection before updating
      const { from, to } = editor.state.selection;

      editor.commands.setContent(html);
      lastContentRef.current = content;
      lastOutputHtmlRef.current = html;

      // Try to restore cursor position if it was valid
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        try {
          const docSize = editor.state.doc.content.size;
          if (from <= docSize && to <= docSize) {
            editor.commands.setTextSelection({ from, to });
          }
        } catch (e) {
          // Selection restoration failed, cursor will be at default position
        }
      }, 0);
    };

    processHtml(markdownResult);
  }, [content, editor]);
  if (!editor) {
    return null;
  }

  return (
    <div className="wysiwyg-editor">
      <div className="sticky top-0 z-[1] bg-white" {...getCollapseProps()}>
        <Toolbar editor={editor} />
      </div>
      <div className="editor-content overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default WysiwygEditor;
